import { supabase } from '@/utils/supabase';
import { v4 as uuidv4 } from 'uuid';

// Storage bucket names
export const PHOTOS_BUCKET = 'hotrod-photos';
export const VIDEO_BUCKET = 'video-bucket';
export const SITE_GRAPHICS_BUCKET = 'site-graphics';

// Track initialization status to prevent duplicate initializations
const storageInitStatus = {
  photos: false,
  videos: false,
  siteGraphics: false
};

// Definition of required image metadata
export interface PhotoMetadata {
  title: string;
  location?: string;
  year?: string;
  category: string;
  userId: number;
}

// Definition of required video metadata
export interface VideoMetadata {
  title: string;
  category: string;
  userId: string | number; // Accept both string and number for flexibility
  description?: string;
}

/**
 * Upload an image to Supabase Storage
 * 
 * @param file File to upload
 * @param metadata Photo metadata
 * @returns Promise with the image URL or error
 */
export async function uploadPhoto(file: File, metadata: PhotoMetadata) {
  try {
    // Validate the file
    if (!file) {
      throw new Error('No file selected');
    }

    // Only allow image files
    if (!file.type.match('image.*')) {
      throw new Error('Please select an image file');
    }

    // Create a unique filename to prevent collisions
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `${metadata.category}/${fileName}`;

    // Upload the file to Supabase Storage
    const { data, error } = await supabase.storage
      .from(PHOTOS_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      if (error.message?.includes('row-level security policy')) {
        throw new Error('Permission denied: You need to be logged in to upload photos');
      }
      throw error;
    }

    // Get the public URL for the file
    const { data: { publicUrl } } = supabase.storage
      .from(PHOTOS_BUCKET)
      .getPublicUrl(data.path);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading photo:', error);
    throw error;
  }
}

/**
 * Delete a photo from Supabase Storage
 * 
 * @param path Full path to the photo in the bucket
 * @returns Promise with success or error
 */
export async function deletePhoto(path: string) {
  try {
    // Extract just the path part (without the bucket URL)
    const pathParts = path.split(`${PHOTOS_BUCKET}/`);
    const filePath = pathParts.length > 1 ? pathParts[1] : path;

    const { error } = await supabase.storage
      .from(PHOTOS_BUCKET)
      .remove([filePath]);

    if (error) {
      if (error.message?.includes('row-level security policy')) {
        throw new Error('Permission denied: You need to be logged in to delete photos');
      }
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
}

/**
 * List all photos in a category from Supabase Storage
 * 
 * @param category Category to list photos from, or 'all' for all photos
 * @returns Promise with list of file paths
 */
export async function listPhotos(category?: string) {
  try {
    console.log(`Listing photos in category: ${category || 'all'}`);
    const options: { limit?: number; search?: string } = { limit: 100 };
    
    // If category is specified, filter by prefix
    if (category && category !== 'all') {
      options.search = category;
    }

    const { data, error } = await supabase.storage
      .from(PHOTOS_BUCKET)
      .list('', options);

    if (error) {
      if (error.message?.includes('row-level security policy')) {
        console.warn('Permission issue when listing photos. RLS policies may not be set up correctly.');
        return [];
      }
      throw error;
    }

    console.log(`Found ${data?.length || 0} items in bucket`);
    return data;
  } catch (error) {
    console.error('Error listing photos:', error);
    // Return empty array to avoid UI breaks
    return [];
  }
}

/**
 * Upload a video to Supabase Storage
 * 
 * @param file File to upload
 * @param metadata Video metadata
 * @returns Promise with the video URL or error
 */
export async function uploadVideo(file: File, metadata: VideoMetadata) {
  try {
    // Validate the file
    if (!file) {
      throw new Error('No file selected');
    }

    // Only allow video files
    if (!file.type.match('video.*')) {
      throw new Error('Please select a video file');
    }

    // Create a unique filename to prevent collisions
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `${metadata.category}/${fileName}`;

    // Upload the file to Supabase Storage
    const { data, error } = await supabase.storage
      .from(VIDEO_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      if (error.message?.includes('row-level security policy')) {
        throw new Error('Permission denied: You need to be logged in to upload videos');
      }
      throw error;
    }

    // Get the public URL for the file
    const { data: { publicUrl } } = supabase.storage
      .from(VIDEO_BUCKET)
      .getPublicUrl(data.path);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading video:', error);
    throw error;
  }
}

/**
 * Delete a video from Supabase Storage
 * 
 * @param path Full path to the video in the bucket
 * @returns Promise with success or error
 */
export async function deleteVideo(path: string) {
  try {
    // Extract just the path part (without the bucket URL)
    const pathParts = path.split(`${VIDEO_BUCKET}/`);
    const filePath = pathParts.length > 1 ? pathParts[1] : path;

    const { error } = await supabase.storage
      .from(VIDEO_BUCKET)
      .remove([filePath]);

    if (error) {
      if (error.message?.includes('row-level security policy')) {
        throw new Error('Permission denied: You need to be logged in to delete videos');
      }
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting video:', error);
    throw error;
  }
}

/**
 * List all videos in a category from Supabase Storage
 * 
 * @param category Category to list videos from
 * @returns Promise with list of file paths
 */
export async function listVideos(category: string) {
  try {
    console.log(`Listing videos in category: ${category}`);
    
    const { data, error } = await supabase.storage
      .from(VIDEO_BUCKET)
      .list(category, { limit: 100 });

    if (error) {
      if (error.message?.includes('row-level security policy')) {
        console.warn('Permission issue when listing videos. RLS policies may not be set up correctly.');
        return [];
      }
      throw error;
    }

    console.log(`Found ${data?.length || 0} videos in bucket/${category}`);
    return data;
  } catch (error) {
    console.error('Error listing videos:', error);
    // Return empty array to avoid UI breaks
    return [];
  }
}

/**
 * Initialize Video Storage - call this when setting up the app
 * Verifies access to the video bucket
 * Will only initialize once per app session
 */
export async function initializeVideoStorage() {
  try {
    // Skip if already initialized
    if (storageInitStatus.videos) {
      console.log(`Video storage already initialized, skipping`);
      return { success: true, alreadyInitialized: true };
    }
    
    console.log(`Verifying access to video bucket: ${VIDEO_BUCKET}`);
    
    // Just check if we can list the bucket contents
    const { data, error } = await supabase.storage
      .from(VIDEO_BUCKET)
      .list('', { limit: 1 });
    
    if (error) {
      // Log the error but don't throw it - the app should still work
      console.warn(`Video storage access warning: ${error.message}`);
      console.warn('This might be due to missing RLS policies. See docs/supabaseai.md for setup instructions.');
      
      // Return success anyway to prevent app initialization failure
      storageInitStatus.videos = true; // Mark as initialized despite warning
      return { success: true, warning: error.message };
    }
    
    console.log(`Successfully verified access to video bucket: ${VIDEO_BUCKET}`);
    storageInitStatus.videos = true; // Mark as successfully initialized
    return { success: true };
  } catch (error) {
    console.error('Error initializing video storage:', error);
    // Log the error but don't throw it to avoid app crashes
    return { success: false, error };
  }
}

/**
 * Utility functions for working with site graphics stored in Supabase
 */
export const siteGraphicsService = {
  /**
   * Get a public URL for a site graphic
   * @param path The path to the file within the site-graphics bucket
   * @returns Public URL string for the graphic
   */
  getPublicUrl: (path: string) => {
    const { data } = supabase.storage
      .from(SITE_GRAPHICS_BUCKET)
      .getPublicUrl(path);
    return data.publicUrl;
  },

  /**
   * Upload a new site graphic (admin only function)
   * @param path The path where the file should be stored (including filename)
   * @param file The file to upload
   * @returns Promise with upload result
   */
  uploadGraphic: async (path: string, file: File) => {
    if (!file) {
      throw new Error('No file selected');
    }

    // Only allow image files
    if (!file.type.match('image.*')) {
      throw new Error('Please select an image file');
    }

    return supabase.storage
      .from(SITE_GRAPHICS_BUCKET)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: true // Allow overwriting existing files
      });
  },

  /**
   * List all graphics in a specific folder
   * @param folder The folder path to list (optional)
   * @returns Promise with list of files
   */
  listGraphics: async (folder: string = '') => {
    try {
      const { data, error } = await supabase.storage
        .from(SITE_GRAPHICS_BUCKET)
        .list(folder);

      if (error) {
        console.error('Error listing site graphics:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error listing site graphics:', error);
      return [];
    }
  },

  /**
   * Delete a site graphic
   * @param path Path to the file to delete
   * @returns Promise with deletion result
   */
  deleteGraphic: async (path: string) => {
    return supabase.storage
      .from(SITE_GRAPHICS_BUCKET)
      .remove([path]);
  },

  /**
   * Initialize the site-graphics bucket - verifies access
   * Will only initialize once per app session
   * @returns Promise with initialization result
   */
  initialize: async () => {
    try {
      // Skip if already initialized
      if (storageInitStatus.siteGraphics) {
        console.log(`Site graphics storage already initialized, skipping`);
        return { success: true, alreadyInitialized: true };
      }
      
      console.log(`Verifying access to site graphics bucket: ${SITE_GRAPHICS_BUCKET}`);
      
      // Check if we can list the bucket contents
      const { data, error } = await supabase.storage
        .from(SITE_GRAPHICS_BUCKET)
        .list('', { limit: 1 });
      
      if (error) {
        console.warn(`Site graphics bucket access warning: ${error.message}`);
        storageInitStatus.siteGraphics = true; // Mark as initialized despite warning
        return { success: false, warning: error.message };
      }
      
      console.log(`Successfully verified access to site graphics bucket: ${SITE_GRAPHICS_BUCKET}`);
      storageInitStatus.siteGraphics = true; // Mark as successfully initialized
      return { success: true };
    } catch (error) {
      console.error('Error initializing site graphics storage:', error);
      return { success: false, error };
    }
  }
};

/**
 * Initialize Supabase Storage - call this when setting up the app
 * Assumes the bucket already exists and just verifies access
 * Will only initialize once per app session
 */
export async function initializeStorage() {
  try {
    // Skip if already initialized
    if (storageInitStatus.photos) {
      console.log(`Photo storage already initialized, skipping`);
      return { success: true, alreadyInitialized: true };
    }
    
    console.log(`Verifying access to storage bucket: ${PHOTOS_BUCKET}`);
    
    // Just check if we can list the bucket contents
    const { data, error } = await supabase.storage
      .from(PHOTOS_BUCKET)
      .list('', { limit: 1 });
    
    if (error) {
      // Log the error but don't throw it - the app should still work
      console.warn(`Storage access warning: ${error.message}`);
      console.warn('This might be due to missing RLS policies. See docs/supabaseai.md for setup instructions.');
      
      // Return success anyway to prevent app initialization failure
      storageInitStatus.photos = true; // Mark as initialized despite warning
      return { success: true, warning: error.message };
    }
    
    console.log(`Successfully verified access to bucket: ${PHOTOS_BUCKET}`);
    storageInitStatus.photos = true; // Mark as successfully initialized
    return { success: true };
  } catch (error) {
    console.error('Error initializing storage:', error);
    // Log the error but don't throw it to avoid app crashes
    return { success: false, error };
  }
}
